import React, { useMemo, memo, useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

interface DashboardChartsProps {
  leadsByStage: {
    acolhimento: number;
    qualificacao: number;
    educacao: number;
    objecoes: number;
    proposta: number;
    agendamento: number;
    followup: number;
    recuperacao: number;
    outros: number;
  };
  appointmentsByStatus: {
    agendado: number;
    confirmado: number;
    realizado: number;
    cancelado: number;
    remarcado: number;
  };
  weeklyTrendData?: {
    day: string;
    leads: number;
    appointments: number;
  }[];
}

const DashboardCharts: React.FC<DashboardChartsProps> = memo(({
  leadsByStage,
  appointmentsByStatus,
  weeklyTrendData
}) => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    checkTheme();
    
    // Observer para mudanças no tema
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const chartTextColor = isDarkMode ? '#ffffff' : '#000000';
  // Dados para o gráfico de leads por estágio - usando useMemo para otimização
  const leadsData = useMemo(() => [
    { name: 'Acolhimento', value: leadsByStage.acolhimento, color: '#37a0e1' },
    { name: 'Qualificação', value: leadsByStage.qualificacao, color: '#5ba3f5' },
    { name: 'Educação', value: leadsByStage.educacao, color: '#7db3f7' },
    { name: 'Objeções', value: leadsByStage.objecoes, color: '#9fc3f9' },
    { name: 'Proposta', value: leadsByStage.proposta, color: '#c1d3fb' },
    { name: 'Agendamento', value: leadsByStage.agendamento, color: '#4a9de8' },
    { name: 'Follow-up', value: leadsByStage.followup, color: '#2e8dd4' },
    { name: 'Recuperação', value: leadsByStage.recuperacao, color: '#6bb0f2' },
    { name: 'Outros', value: leadsByStage.outros, color: '#8cc5f3' }
  ].filter(item => item.value > 0), [leadsByStage]);

  // Dados para o gráfico de agendamentos por status - usando useMemo para otimização
  const appointmentsData = useMemo(() => [
    { name: 'Agendado', value: appointmentsByStatus.agendado, color: '#37a0e1' },
    { name: 'Confirmado', value: appointmentsByStatus.confirmado, color: '#5ba3f5' },
    { name: 'Realizado', value: appointmentsByStatus.realizado, color: '#2e8dd4' },
    { name: 'Cancelado', value: appointmentsByStatus.cancelado, color: '#9fc3f9' },
    { name: 'Remarcado', value: appointmentsByStatus.remarcado, color: '#7db3f7' }
  ].filter(item => item.value > 0), [appointmentsByStatus]);

  // Dados para gráfico de tendência (últimos 7 dias) - usando dados reais ou fallback
  const trendData = useMemo(() => {
    if (weeklyTrendData && weeklyTrendData.length > 0) {
      return weeklyTrendData;
    }
    // Fallback para dados simulados se não houver dados reais
    return [
      { day: 'Seg', leads: 0, appointments: 0 },
      { day: 'Ter', leads: 0, appointments: 0 },
      { day: 'Qua', leads: 0, appointments: 0 },
      { day: 'Qui', leads: 0, appointments: 0 },
      { day: 'Sex', leads: 0, appointments: 0 },
      { day: 'Sáb', leads: 0, appointments: 0 },
      { day: 'Dom', leads: 0, appointments: 0 }
    ];
  }, [weeklyTrendData]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card p-3 border border-border rounded-lg shadow-lg">
          <p className="font-medium text-foreground">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomPieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-card p-3 border border-border rounded-lg shadow-lg">
          <p className="font-medium text-foreground">{data.name}</p>
          <p className="text-sm text-muted-foreground">Quantidade: {data.value}</p>
        </div>
      );
    }
    return null;
  };

  const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill={chartTextColor} 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {`${name} ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
      {/* Gráfico de Leads por Estágio */}
      <Card className="col-span-1 border border-border transition-all duration-300">
        <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="flex items-center">
            <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <CardTitle className="text-lg font-bold text-foreground">Leads por Estágio</CardTitle>
          </div>
          <div className="bg-primary p-2 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={leadsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartTextColor} opacity={0.2} />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12, fill: chartTextColor }}
                angle={-45}
                textAnchor="end"
                height={80}
                axisLine={{ stroke: chartTextColor }}
                tickLine={{ stroke: chartTextColor }}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: chartTextColor }} 
                axisLine={{ stroke: chartTextColor }}
                tickLine={{ stroke: chartTextColor }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar 
                dataKey="value" 
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
                className="hover:opacity-80 transition-opacity"
                animationBegin={0}
                animationDuration={1500}
                animationEasing="ease-out"
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Gráfico de Agendamentos por Status */}
      <Card className="col-span-1 border border-border transition-all duration-300">
        <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="flex items-center">
            <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <CardTitle className="text-lg font-bold text-foreground">Agendamentos por Status</CardTitle>
          </div>
          <div className="bg-primary p-2 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
            </svg>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={appointmentsData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={CustomPieLabel}
                labelLine={false}
                animationBegin={200}
                animationDuration={1800}
                animationEasing="ease-in-out"
              >
                {appointmentsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Gráfico de Tendência Semanal */}
      <Card className="col-span-1 lg:col-span-2 border border-border transition-all duration-300">
        <CardHeader className="bg-accent border-b border-border flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="flex items-center">
            <div className="bg-primary h-10 w-10 rounded-xl shadow-lg flex items-center justify-center mr-3">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <CardTitle className="text-lg font-bold text-foreground">Tendência Semanal</CardTitle>
          </div>
          <div className="bg-primary p-2 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trendData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartTextColor} opacity={0.2} />
              <XAxis 
                dataKey="day" 
                axisLine={{ stroke: chartTextColor, strokeWidth: 1 }}
                tickLine={{ stroke: chartTextColor, strokeWidth: 1 }}
                tick={{ fill: chartTextColor, fontSize: 12 }}
              />
              <YAxis 
                axisLine={{ stroke: chartTextColor, strokeWidth: 1 }}
                tickLine={{ stroke: chartTextColor, strokeWidth: 1 }}
                tick={{ fill: chartTextColor, fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="leads"
                stackId="1"
                stroke="#37a0e1"
                fill="#37a0e1"
                fillOpacity={0.6}
                name="Leads"
                animationBegin={400}
                animationDuration={2000}
                animationEasing="ease-out"
              />
              <Area
                type="monotone"
                dataKey="appointments"
                stackId="1"
                stroke="#5ba3f5"
                fill="#5ba3f5"
                fillOpacity={0.4}
                name="Agendamentos"
                animationBegin={600}
                animationDuration={2000}
                animationEasing="ease-out"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
});

export default DashboardCharts;